import {CalendarDays, Clock, Wallet, Zap, TrendingUp} from "lucide-react";
import {Progress} from "@/components/ui/progress";
import Badge from "@/components/Badge";
import {FC} from "react";

interface ShredDetailsProps {
  shredName: string;
  shredProgress: number;
  shredStartDate: string;
  shredCoinsUsed: string;
  shredGenerationTime: string;
  isShredMode: boolean
}

const ShredDetails: FC<ShredDetailsProps> = (props) => {
  const {shredName, shredProgress, shredStartDate, shredCoinsUsed, shredGenerationTime, isShredMode} = props;

  return (
      <div className="col-span-12 lg:col-span-6">
        <div className="relative bg-gradient-to-br from-slate-50 to-blue-50/50 border border-slate-200/60 rounded-2xl p-6 shadow-lg backdrop-blur-sm h-[280px] flex flex-col overflow-hidden">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 rounded-2xl"></div>

          {/* Header */}
          <div className="relative z-10 flex items-center justify-between mb-5">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2.5 bg-blue-100/70 backdrop-blur-sm rounded-xl border border-blue-200/50">
                <TrendingUp className="w-5 h-5 text-blue-700" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-bold text-slate-800 truncate leading-tight">
                  {shredName}
                </h1>
                <p className="text-sm text-slate-600 font-medium">Project Analytics</p>
              </div>
            </div>
            {isShredMode && (
                <Badge size="sm" classColor="bg-amber-100/80 text-amber-800 border border-amber-300/50 backdrop-blur-sm">
                  <Zap className="w-3 h-3 mr-1" />
                  Shred Model
                </Badge>
            )}
          </div>

          {/* Progress Section */}
          <div className="relative z-10 mb-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-slate-700">Overall Progress</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-lg font-bold text-slate-800">{shredProgress}%</span>
              </div>
            </div>
            <div className="relative">
              <Progress
                  value={shredProgress}
                  max={100}
                  className="h-3 bg-slate-200/60 rounded-full border border-slate-300/30"
              />
            </div>
          </div>

          {/* Stats Grid */}
          <div className="relative z-10 grid grid-cols-3 gap-3 flex-1">
            {/* Start Date */}
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-slate-200/50 hover:bg-white/80 transition-all duration-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-emerald-100/70 rounded-lg">
                  <CalendarDays className="w-3.5 h-3.5 text-emerald-700" />
                </div>
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Start</span>
              </div>
              <p className="text-sm font-bold text-slate-800 truncate">{shredStartDate}</p>
            </div>

            {/* Coins Used */}
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-slate-200/50 hover:bg-white/80 transition-all duration-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-amber-100/70 rounded-lg">
                  <Wallet className="w-3.5 h-3.5 text-amber-700" />
                </div>
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">CREDITS</span>
              </div>
              <p className="text-sm font-bold text-slate-800 truncate">{shredCoinsUsed}</p>
            </div>

            {/* Generation Time */}
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-slate-200/50 hover:bg-white/80 transition-all duration-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-rose-100/70 rounded-lg">
                  <Clock className="w-3.5 h-3.5 text-rose-700" />
                </div>
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Time</span>
              </div>
              <p className="text-sm font-bold text-slate-800 truncate">{shredGenerationTime}</p>
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-blue-200/20 to-transparent rounded-full -translate-y-12 translate-x-12"></div>
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-purple-200/20 to-transparent rounded-full translate-y-10 -translate-x-10"></div>
        </div>
      </div>
  );
};

export default ShredDetails;